import { formatHumanName, type MedplumClient } from '@medplum/core';
import type {
  Account,
  Appointment as FhirAppointment,
  DocumentReference,
  EpisodeOfCare,
  HumanName,
  Observation,
  Organization,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
  Reference,
  RelatedPerson,
  ServiceRequest,
  Task as FhirTask,
} from '@medplum/fhirtypes';
import {
  assessmentCoverageKey,
  assessmentTypeLabelFromQuestionnaire,
  buildAssessmentTrendDataPoints,
  isMeasuresQuestionnaire,
  loincCodeForQuestionnaire,
  LOINC_ASSESSMENT_SCORE,
  matchObservationForQuestionnaireResponse,
  observationToAssessmentView,
  resolveAssessmentScore,
  resolveInterpretationLabel,
  resolveMaxScoreForQuestionnaire,
} from './assessmentMedplumAdapters';
import { EPISODE_OF_CARE_CONTEXT_EXTENSION, THERAPY_SERVICE_TYPE_SYSTEM } from './caseloadFhirConstants';
import { patientCompartmentSearch } from './patientCoverageSearch';
import {
  HISTORICAL_MIGRATION_TAG_CODES,
  HISTORICAL_MIGRATION_TAG_SYSTEM,
  PARTICIPANT_PROFILE_EXT,
} from '@unify/fhir-model/constants';
import {
  getEpisodeOfCareIdFromDocumentReference,
  getHistoricalMedicalRecordType,
  isHistoricalChartMigrationDocumentReference,
} from '@unify/fhir-model/resources/sp-historical-document';
import { isPsychotherapyNote } from '@unify/fhir-model/resources/document-reference';
import { getLinkedTaskId } from '@unify/fhir-model/resources/questionnaire-response';
import { composedChartNoteType, composedNoteTitle } from './chart/composedChartNote';
import { getParticipantRefs } from '@unify/fhir-model/resources/episode-of-care';
import { getRelationshipCodes } from '@unify/fhir-model/resources/related-person';
import { taskRequesterLabel } from './taskAssignerDisplay';
import {
  getAppointmentIdFromQR,
  getRequestedEditsFromQR,
  CLIENT_SIGNATURE_EXT_URL,
  getSignatureFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
} from '../pages/appointments/fhirNoteMapping';
import { formatPractitionerStaffLabel, practitionerRequiresCosignature } from '@unify/fhir-model/resources/practitioner';
import {
  getAppointmentSharedNoteRef,
  isCohortGroupAppointment,
} from './groupTherapy/groupSharedNote';
import { isHumanParticipantDisplay } from '../pages/appointments/utils/fhirHelpers';
import type { ClientChartBundle } from '../pages/clients/data/clientChartBundle';
import type {
  AdminNote,
  Appointment,
  ClientDocument,
  ClinicalNote,
  CompletedForm,
  DischargeSummary,
  Episode,
  EpisodeOfCareStatus,
  EpisodeParty,
  EpisodeType,
  HistoricalChartRecord,
  PendingFormTask,
  Task,
} from '../pages/clients/data/types';
import {
  getFormTaskDisplayName,
  isFormRequestTask,
  resolveChartFormTaskStatus,
} from './clientFacingTasks';
import {
  isChartDocumentQuestionnaireResponse,
  isContactRoiQuestionnaireResponse,
  deriveChartDocumentClinicalNoteStatus,
} from './chart/chartDocumentWorkflow';
import { isAppointmentTemplate } from './chart/chartDocumentTemplates';
import { resolveChartDocumentRecordType } from './chart/chartDocumentRecordType';
import { extractNoteContent } from './chart/questionnaireResponseContent';
import { resolveQuestionnaireForResponseRef } from './chart/chartQuestionnaireRef';
import {
  isClientFacingDocumentQuestionnaire,
  isClinicalQuestionnaire,
  isClientVisibleDocumentReference,
} from '../pages/client-portal/utils/clientPortalClassifiers';
import {
  codeFromServiceTypeCodeableConcept,
  displayLabelForEpisodeType,
} from './fhirValueSets/sessionAndEpisodeTypes';
import { indexServiceRequestsByEpisodeId } from './transferService';
import { formatDateOnlyLocal } from './dateHelpers';
import { isTransferQuestionnaireResponse } from './transferFhirConstants';
import {
  buildTransferClinicalNoteSoap,
  transferChartNoteStatus,
  transferServiceRenderedLabel,
} from './transferChartNote';

function emptyBundle(): ClientChartBundle {
  return {
    appointments: [],
    clinicalNotes: [],
    adminNotes: [],
    episodes: [],
    medications: [],
    dischargeSummaries: [],
    assessments: [],
    assessmentTrends: [],
    healthMetrics: [],
    completedForms: [],
    documents: [],
    historicalRecords: [],
    tasks: [],
    outstandingForms: [],
    pendingFormTasks: [],
  };
}

function uiEpisodeStatus(s: string | undefined): EpisodeOfCareStatus {
  switch (s) {
    case 'planned':
      return 'planned';
    case 'waitlist':
      return 'waitlist';
    case 'active':
      return 'active';
    case 'finished':
      return 'finished';
    case 'onhold':
      return 'follow-up';
    default:
      return 'active';
  }
}



function therapyServiceTypeToEpisodeType(typeCode: string | undefined): EpisodeType | undefined {
  switch (typeCode) {
    case 'individual':
    case 'group':
    case 'family':
    case 'couples':
    case 'medication-management':
      return typeCode;
    default:
      return undefined;
  }
}

function fhirEpisodeToEpisode(
  eoc: EpisodeOfCare,
  careManagerNameById?: ReadonlyMap<string, string>,
): Episode {
  const start = eoc.period?.start?.slice(0, 10) ?? '2026-01-01';
  const end = eoc.period?.end?.slice(0, 10);
  const typeCc = eoc.type?.[0];
  const name = displayLabelForEpisodeType(typeCc);
  const typeCode = codeFromServiceTypeCodeableConcept(typeCc);
  const episodeType = therapyServiceTypeToEpisodeType(typeCode);
  const careManagerPractitionerId = eoc.careManager?.reference?.startsWith('Practitioner/')
    ? eoc.careManager.reference.replace('Practitioner/', '')
    : undefined;
  const storedDisplay = eoc.careManager?.display?.trim();
  const provider =
    (careManagerPractitionerId ? careManagerNameById?.get(careManagerPractitionerId) : undefined) ??
    (isHumanParticipantDisplay(storedDisplay, careManagerPractitionerId)
      ? storedDisplay
      : undefined) ??
    'Provider';
  return {
    id: eoc.id ?? 'eoc',
    name,
    startDate: start,
    endDate: end,
    status: uiEpisodeStatus(eoc.status),
    provider,
    clinicalDomain: typeCode === 'iop' ? 'iop' : 'outpatient',
    ...(typeCode ? { serviceTypeCode: typeCode } : {}),
    ...(episodeType ? { episodeType } : {}),
    ...(careManagerPractitionerId ? { careManagerPractitionerId } : {}),
  };
}

function parseFhirResourceReferenceString(
  ref: string | { reference?: string } | undefined,
): { type: string; id: string } | undefined {
  const str = typeof ref === 'string' ? ref : ref?.reference;
  if (!str) return undefined;
  const i = str.indexOf('/');
  if (i < 0) return undefined;
  return { type: str.slice(0, i), id: str.slice(i + 1) };
}

function fhirNameListDisplay(names: HumanName[] | undefined, fallback: string): string {
  if (!names?.length) return fallback;
  const withText = names.find((n) => n.text);
  if (withText?.text) return withText.text;
  return formatHumanName(names[0]) || fallback;
}

/**
 * Preload Account resources linked from EpisodeOfCare (for guarantor party).
 */
async function readAccountsByIds(medplum: MedplumClient, accountIds: string[]): Promise<Map<string, Account>> {
  const m = new Map<string, Account>();
  await Promise.all(
    accountIds.map(async (id) => {
      try {
        m.set(id, await medplum.readResource('Account', id));
      } catch {
        /* missing or denied */
      }
    }),
  );
  return m;
}

async function loadPatientsById(medplum: MedplumClient, ids: string[]): Promise<Map<string, Patient>> {
  const unique = [...new Set(ids.map((s) => s?.trim()).filter((x): x is string => Boolean(x)))];
  const m = new Map<string, Patient>();
  if (unique.length === 0) {
    return m;
  }
  const batch = await medplum.searchResources('Patient', { _id: unique.join(',') } as Record<string, string>);
  for (const p of batch) {
    if (p.id) m.set(p.id, p);
  }
  return m;
}

async function loadRelatedPersonsById(
  medplum: MedplumClient,
  ids: string[],
): Promise<Map<string, RelatedPerson>> {
  const unique = [...new Set(ids.map((s) => s?.trim()).filter((x): x is string => Boolean(x)))];
  const m = new Map<string, RelatedPerson>();
  if (unique.length === 0) {
    return m;
  }
  const batch = await medplum.searchResources('RelatedPerson', { _id: unique.join(',') } as Record<string, string>);
  for (const p of batch) {
    if (p.id) m.set(p.id, p);
  }
  return m;
}

/** Practitioner id → staff display name for episode care managers. */
async function loadPractitionerNamesById(
  medplum: MedplumClient,
  ids: string[],
): Promise<Map<string, string>> {
  const unique = [...new Set(ids.map((s) => s?.trim()).filter((x): x is string => Boolean(x)))];
  const m = new Map<string, string>();
  if (unique.length === 0) {
    return m;
  }
  const batch = await medplum
    .searchResources('Practitioner', { _id: unique.join(',') } as Record<string, string>)
    .catch(() => [] as Practitioner[]);
  for (const p of batch) {
    if (!p.id) continue;
    const name = formatPractitionerStaffLabel(p);
    if (name) m.set(p.id, name);
  }
  return m;
}

async function loadOrganizationsById(medplum: MedplumClient, ids: string[]): Promise<Map<string, Organization>> {
  const unique = [...new Set(ids.map((s) => s?.trim()).filter((x): x is string => Boolean(x)))];
  const m = new Map<string, Organization>();
  if (unique.length === 0) {
    return m;
  }
  const batch = await medplum.searchResources('Organization', { _id: unique.join(',') } as Record<string, string>);
  for (const p of batch) {
    if (p.id) m.set(p.id, p);
  }
  return m;
}

function getGuarantorPartyRef(eoc: EpisodeOfCare, accountById: Map<string, Account>): Reference | undefined {
  for (const ar of eoc.account ?? []) {
    if (!ar?.reference) {
      continue;
    }
    const aid = parseFhirResourceReferenceString(ar);
    if (aid?.type !== 'Account' || !aid.id) {
      continue;
    }
    const acc = accountById.get(aid.id);
    const p = acc?.guarantor?.[0]?.party;
    if (p?.reference) {
      return p;
    }
  }
  return undefined;
}

/**
 * Resolves a FHIR party reference to an EpisodeParty label for the EoC panel.
 * Prefers loaded `Patient` / `RelatedPerson` / `Organization` data, then `Reference.display`.
 */
function referenceToEpisodeParty(
  ref: Reference | undefined,
  role: 'primary' | 'guarantor',
  chartPatientId: string,
  chartNameFallback: string,
  patients: Map<string, Patient>,
  relatedPersons: Map<string, RelatedPerson>,
  organizations: Map<string, Organization>,
): EpisodeParty {
  if (!ref?.reference) {
    return {
      id: chartPatientId,
      name: chartNameFallback,
      relationship: 'Self',
    };
  }
  const parsed = parseFhirResourceReferenceString(ref);
  if (!parsed) {
    return {
      id: chartPatientId,
      name: ref.display?.trim() || chartNameFallback,
      relationship: 'Self',
    };
  }
  if (parsed.type === 'Patient') {
    const p = patients.get(parsed.id);
    const fromResource = p ? fhirNameListDisplay(p.name, chartNameFallback) : '';
    const name = fromResource || ref.display?.trim() || chartNameFallback;
    return {
      id: parsed.id,
      name,
      relationship: role === 'primary' ? 'Self' : 'Guarantor',
    };
  }
  if (parsed.type === 'RelatedPerson') {
    const p = relatedPersons.get(parsed.id);
    const name = p
      ? fhirNameListDisplay(p.name, ref.display?.trim() || 'Guarantor')
      : ref.display?.trim() || 'Guarantor';
    return {
      id: `relatedperson-${parsed.id}`,
      name,
      relationship: role === 'guarantor' ? 'Guarantor' : 'Self',
    };
  }
  if (parsed.type === 'Organization') {
    const o = organizations.get(parsed.id);
    return {
      id: `org-${parsed.id}`,
      name: o?.name || ref.display?.trim() || 'Account',
      relationship: 'Guarantor',
    };
  }
  return {
    id: chartPatientId,
    name: ref.display?.trim() || chartNameFallback,
    relationship: 'Self',
  };
}

function relationshipDisplayFromRelatedPerson(rp: RelatedPerson | undefined): string | undefined {
  const code = rp ? getRelationshipCodes(rp)[0] : undefined;
  switch (code) {
    case 'SPS':
      return 'Spouse';
    case 'FTH':
      return 'Father';
    case 'MTH':
      return 'Mother';
    case 'GUARD':
      return 'Guardian';
    case 'PRN':
      return 'Parent';
    default:
      return undefined;
  }
}

function buildGroupMembersForEpisode(
  eoc: EpisodeOfCare,
  primaryParty: EpisodeParty,
  patients: Map<string, Patient>,
  relatedPersons: Map<string, RelatedPerson>,
): EpisodeParty[] {
  const members: EpisodeParty[] = [primaryParty];
  for (const ref of getParticipantRefs(eoc)) {
    const parsed = parseFhirResourceReferenceString(ref);
    if (parsed?.type !== 'RelatedPerson' || !parsed.id) continue;
    const rp = relatedPersons.get(parsed.id);
    const linkedRef = rp?.extension?.find((e) => e.url === PARTICIPANT_PROFILE_EXT)?.valueReference
      ?.reference;
    const linked = parseFhirResourceReferenceString(linkedRef);
    const rel = relationshipDisplayFromRelatedPerson(rp);
    if (linked?.type === 'Patient' && linked.id) {
      const p = patients.get(linked.id);
      members.push({
        id: linked.id,
        name: p ? fhirNameListDisplay(p.name, ref.display?.trim() || 'Participant') : ref.display?.trim() || 'Participant',
        ...(rel ? { relationship: rel } : {}),
        relatedPersonId: parsed.id,
      });
    } else {
      members.push({
        id: `relatedperson-${parsed.id}`,
        name: rp
          ? fhirNameListDisplay(rp.name, ref.display?.trim() || 'Participant')
          : ref.display?.trim() || 'Participant',
        ...(rel ? { relationship: rel } : {}),
        relatedPersonId: parsed.id,
      });
    }
  }
  return members;
}

/**
 * Fetches Patient / Account-linked guarantor / RelatedPerson / Organization for EpisodeOfCare
 * and fills Primary Client + Guarantor names (replaces the prior hardcoded "Patient" label).
 */
async function buildEpisodesWithResolvedParties(
  medplum: MedplumClient,
  eocs: EpisodeOfCare[],
  chartPatientId: string,
): Promise<Episode[]> {
  if (eocs.length === 0) {
    return [];
  }
  const accountIdSet = new Set<string>();
  for (const e of eocs) {
    for (const ar of e.account ?? []) {
      if (!ar?.reference) continue;
      const a = parseFhirResourceReferenceString(ar.reference);
      if (a?.type === 'Account' && a.id) {
        accountIdSet.add(a.id);
      }
    }
  }
  const accountById = await readAccountsByIds(medplum, [...accountIdSet]);
  const patientIdSet = new Set<string>([chartPatientId]);
  const relatedIdSet = new Set<string>();
  const orgIdSet = new Set<string>();
  for (const e of eocs) {
    const pref = e.patient?.reference
      ? parseFhirResourceReferenceString(e.patient.reference)
      : { type: 'Patient', id: chartPatientId };
    if (pref?.type === 'Patient' && pref.id) {
      patientIdSet.add(pref.id);
    }
    const g = getGuarantorPartyRef(e, accountById);
    if (g?.reference) {
      const pr = parseFhirResourceReferenceString(g.reference);
      if (pr?.type === 'Patient' && pr.id) patientIdSet.add(pr.id);
      if (pr?.type === 'RelatedPerson' && pr.id) relatedIdSet.add(pr.id);
      if (pr?.type === 'Organization' && pr.id) orgIdSet.add(pr.id);
    }
    for (const pref of getParticipantRefs(e)) {
      const pr = parseFhirResourceReferenceString(pref.reference);
      if (pr?.type === 'RelatedPerson' && pr.id) relatedIdSet.add(pr.id);
    }
  }
  const careManagerIds = eocs
    .map((e) => e.careManager?.reference)
    .filter((ref): ref is string => Boolean(ref?.startsWith('Practitioner/')))
    .map((ref) => ref.replace('Practitioner/', ''));
  const [patientById, relatedById, orgById, careManagerNameById] = await Promise.all([
    loadPatientsById(medplum, [...patientIdSet]),
    loadRelatedPersonsById(medplum, [...relatedIdSet]),
    loadOrganizationsById(medplum, [...orgIdSet]),
    loadPractitionerNamesById(medplum, careManagerIds),
  ]);
  const linkedPatientIds = new Set<string>();
  for (const rp of relatedById.values()) {
    const linkedRef = rp.extension?.find((e) => e.url === PARTICIPANT_PROFILE_EXT)?.valueReference?.reference;
    const parsed = parseFhirResourceReferenceString(linkedRef);
    if (parsed?.type === 'Patient' && parsed.id && !patientById.has(parsed.id)) {
      linkedPatientIds.add(parsed.id);
    }
  }
  if (linkedPatientIds.size > 0) {
    const extraPatients = await loadPatientsById(medplum, [...linkedPatientIds]);
    for (const [id, patient] of extraPatients) {
      patientById.set(id, patient);
    }
  }
  const chartName = fhirNameListDisplay(patientById.get(chartPatientId)?.name, 'Patient');
  return eocs.map((eoc) => {
    const base = fhirEpisodeToEpisode(eoc, careManagerNameById);
    const defaultPrimary: Reference = eoc.patient?.reference
      ? eoc.patient
      : { reference: `Patient/${chartPatientId}` };
    const fromAccount = getGuarantorPartyRef(eoc, accountById);
    const effectiveGuarantorRef: Reference = fromAccount ?? defaultPrimary;
    const primaryParty = referenceToEpisodeParty(
      defaultPrimary,
      'primary',
      chartPatientId,
      chartName,
      patientById,
      relatedById,
      orgById,
    );
    const gSame =
      !fromAccount || (fromAccount.reference != null && fromAccount.reference === defaultPrimary.reference);
    const guarantorParty = gSame
      ? { ...primaryParty, relationship: 'Self' as const }
      : referenceToEpisodeParty(
          effectiveGuarantorRef,
          'guarantor',
          chartPatientId,
          chartName,
          patientById,
          relatedById,
          orgById,
        );
    const groupMembers = buildGroupMembersForEpisode(
      eoc,
      primaryParty,
      patientById,
      relatedById,
    );
    return {
      ...base,
      primaryClinicalPatient: primaryParty,
      guarantor: guarantorParty,
      groupMembers,
    };
  });
}

function episodeIdFromFhirExtension(
  resource: { extension?: Array<{ url?: string; valueReference?: { reference?: string } }> },
): string | undefined {
  const ext = resource.extension?.find((e) => e.url === EPISODE_OF_CARE_CONTEXT_EXTENSION);
  const ref = ext?.valueReference?.reference;
  if (ref?.startsWith('EpisodeOfCare/')) {
    return ref.replace('EpisodeOfCare/', '');
  }
  return undefined;
}

function relatedEpisodeIdFromDocument(doc: DocumentReference): string | undefined {
  return getEpisodeOfCareIdFromDocumentReference(doc);
}

function resolveQuestionnaireResponseEpisodeId(
  qr: QuestionnaireResponse,
  encounterIdToEoc: Map<string, string>,
  apptToEpisode: Map<string, string>,
  fallbackEocId: string | undefined,
): string | undefined {
  const fromExt = episodeIdFromFhirExtension(qr);
  if (fromExt) {
    return fromExt;
  }
  const encId = qr.encounter?.reference?.replace('Encounter/', '');
  if (encId) {
    const fromEnc = encounterIdToEoc.get(encId);
    if (fromEnc) {
      return fromEnc;
    }
  }
  const bo = qr.basedOn?.[0]?.reference;
  if (bo?.startsWith('Appointment/')) {
    const fromAppt = apptToEpisode.get(bo.replace('Appointment/', ''));
    if (fromAppt) {
      return fromAppt;
    }
  }
  return fallbackEocId;
}

export function appointmentInstantToLocalDateTime(
  iso: string | undefined,
): { date: string; time: string } | undefined {
  if (!iso) return undefined;
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return undefined;
  return {
    date: formatDateOnlyLocal(instant),
    time: instant.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

function fhirAppointmentStatus(
  s: FhirAppointment['status'],
): Appointment['status'] {
  switch (s) {
    case 'proposed':
      return 'Requested';
    case 'fulfilled':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'noshow':
      return 'No-Show';
    case 'pending':
    case 'booked':
    case 'arrived':
    case 'checked-in':
    case 'waitlist':
    default:
      return 'Scheduled';
  }
}

/**
 * Detect therapy service type from FHIR Appointment metadata.
 * Used to tag appointments with their modality for display.
 */
function detectTherapyType(a: FhirAppointment): string {
  if (isCohortGroupAppointment(a)) return 'Group';
  const codings = [
    ...(a.serviceType?.[0]?.coding ?? []),
    ...(a.appointmentType?.coding ?? []),
  ];
  for (const c of codings) {
    if (c.system !== THERAPY_SERVICE_TYPE_SYSTEM) continue;
    if (c.code === 'couples' || c.code === 'family') return 'Family';
    if (c.code === 'group' || c.code === 'group-therapy') return 'Group';
    if (c.code === 'individual') return 'Individual';
    if (c.code === 'iop') return 'IOP';
  }

  const text = (
    a.serviceType?.[0]?.text ??
    a.serviceType?.[0]?.coding?.[0]?.display ??
    a.appointmentType?.text ??
    a.appointmentType?.coding?.[0]?.display ??
    ''
  ).toLowerCase();

  if (text.includes('iop') || text.includes('intensive outpatient')) return 'IOP';
  if (text.includes('group')) return 'Group';
  if (text.includes('family') || text.includes('couples')) return 'Family';
  const patientCount = (a.participant ?? []).filter(
    (p) => p.actor?.reference?.startsWith('Patient/'),
  ).length;
  if (patientCount > 1) return 'Group';
  return 'Individual';
}

function isGroupTherapyEpisode(eoc: EpisodeOfCare): boolean {
  return (
    eoc.type?.some((t) =>
      t.coding?.some((c) => c.code === 'group' || c.code === 'group-therapy'),
    ) ?? false
  );
}

/** Prefer linked Encounter/EoC ext; cohort sessions fall back to the patient's group-therapy EoC. */
export function resolveChartAppointmentEpisodeId(
  appointment: FhirAppointment,
  apptToEpisode: Map<string, string>,
  eocs: EpisodeOfCare[],
): string | undefined {
  if (appointment.id) {
    const linked = apptToEpisode.get(appointment.id);
    if (linked) return linked;
  }
  const fromExt = episodeIdFromFhirExtension(appointment);
  if (fromExt) return fromExt;
  if (isCohortGroupAppointment(appointment)) {
    const groupEoc = eocs.find(isGroupTherapyEpisode);
    if (groupEoc?.id) return groupEoc.id;
  }
  return eocs[0]?.id;
}

/** Chart appointment title: EoC type, with cohort Group sessions forced to Group Therapy. */
export function chartAppointmentServiceTypeLabel(
  appointment: FhirAppointment,
  eoc: EpisodeOfCare | undefined,
): string {
  if (isCohortGroupAppointment(appointment)) {
    const fromEoc = eoc ? episodeTypeLabelForAppointment(eoc) : undefined;
    if (fromEoc && /group/i.test(fromEoc)) return fromEoc;
    return 'Group Therapy';
  }
  return episodeTypeLabelForAppointment(eoc);
}

function actorLabel(
  a: FhirAppointment,
  fallback: string,
  practitionersById?: ReadonlyMap<string, Practitioner>,
): string {
  const parts = a.participant ?? [];
  for (const p of parts) {
    const ref = p.actor?.reference;
    if (!ref?.startsWith('Practitioner/')) continue;
    const id = ref.slice('Practitioner/'.length).trim();
    const explicit = p.actor?.display;
    const fromResource = practitionerDisplayName(practitionersById?.get(id), '');
    if (fromResource) return fromResource;
    if (isHumanParticipantDisplay(explicit, id)) return explicit.trim();
  }
  if (isHumanParticipantDisplay(fallback)) return fallback.trim();
  return 'Provider';
}

/** Medical record / chart appointment title — always from linked EpisodeOfCare.type, not Appointment.serviceType. */
export function episodeTypeLabelForAppointment(eoc: EpisodeOfCare | undefined): string {
  return displayLabelForEpisodeType(eoc?.type?.[0]);
}

function fhirAppointmentToUi(
  a: FhirAppointment,
  episodeId: string | undefined,
  providerFallback: string,
  /** True when a QuestionnaireResponse (or other chart note) is linked via appointment tag. */
  hasNote: boolean,
  episodeTypeLabel: string,
  practitionersById?: ReadonlyMap<string, Practitioner>,
): Appointment {
  const start = a.start ?? '';
  const localStart = appointmentInstantToLocalDateTime(start);
  const date = localStart?.date ?? '2026-01-01';
  return {
    id: a.id ?? `apt-${date}`,
    date,
    time: localStart?.time ?? '10:00 AM',
    ...(a.end ? { endIso: a.end } : {}),
    ...(a.minutesDuration != null && a.minutesDuration > 0
      ? { minutesDuration: a.minutesDuration }
      : {}),
    provider: actorLabel(a, providerFallback, practitionersById),
    serviceType: episodeTypeLabel,
    status: fhirAppointmentStatus(a.status),
    episodeId,
    location: { type: 'Telehealth', platform: 'Telehealth' },
    hasNote,
    hasTreatmentPlan: false,
    tags: [detectTherapyType(a)],
  };
}

function practitionerDisplayName(p: Practitioner | undefined, fallback: string): string {
  if (!p) return fallback;
  return formatPractitionerStaffLabel(p) || fallback;
}

/**
 * Chart timeline provider label: prefer the provider signature name when present.
 * Historical SP imports often set QR.author to the mapped migrate practitioner, which may
 * not match who signed the original note/plan.
 */
export function resolveClinicalNoteProviderDisplay(
  qr: QuestionnaireResponse,
  author: Practitioner | undefined,
  primaryProvider: string,
): string {
  const signedName = getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL)?.name?.trim();
  if (signedName) return signedName;
  return practitionerDisplayName(author, primaryProvider);
}

function isSessionDocumentationQr(qr: QuestionnaireResponse): boolean {
  const qUrl = typeof qr.questionnaire === 'string' ? qr.questionnaire.toLowerCase() : '';
  return qUrl.includes('progress-note') || qUrl.includes('intake');
}

/**
 * QuestionnaireResponse id → appointment id for session documentation
 * (meta.tag, or the fulfilled appointment + EoC + same-day fallback).
 *
 * Both the appointment's `hasNote` flag and the note's `appointmentId` come from this map, so a
 * date-matched note can no longer appear twice (once folded into the appointment, once standalone).
 */
function collectChartNoteAppointmentLinks(
  qrs: QuestionnaireResponse[],
  fhirAppts: FhirAppointment[],
  apptToEpisode: Map<string, string>,
): Map<string, string> {
  const links = new Map<string, string>();
  for (const qr of qrs) {
    if (!qr.id) continue;
    // Chart documents are always standalone timeline rows — never fold them into an appointment,
    // even if a stray appointment tag is present.
    if (isChartDocumentQuestionnaireResponse(qr)) continue;
    const fromTag = getAppointmentIdFromQR(qr);
    if (fromTag) {
      links.set(qr.id, fromTag);
      continue;
    }
    if (!isSessionDocumentationQr(qr)) continue;
    const eocId = episodeIdFromFhirExtension(qr);
    const day = qr.authored?.slice(0, 10);
    if (!eocId || !day) continue;
    const matches = fhirAppts.filter((a) => {
      if (!a.id || a.status !== 'fulfilled') return false;
      const aEoc = apptToEpisode.get(a.id) ?? episodeIdFromFhirExtension(a);
      const aDay = a.start?.slice(0, 10);
      return aEoc === eocId && aDay === day;
    });
    if (matches.length === 1 && matches[0].id) {
      links.set(qr.id, matches[0].id);
    }
  }
  return links;
}

function sessionNoteTypeFromQuestionnaire(q: Questionnaire | undefined): ClinicalNote['noteType'] {
  if (!q) return 'Progress Note';
  const t = (q.title ?? q.name ?? '').toLowerCase();
  // Standalone chart/admin notes composed in the Medical Record tab are not session documentation.
  if (t.includes('chart note')) return 'Chart Note';
  if (t.includes('admin note')) return 'Administrative Note';
  if (t.includes('intake')) return 'Intake Note';
  if (t.includes('couples')) return 'Couples Therapy Progress Note';
  if (t.includes('family')) return 'Family Therapy Progress Note';
  if (t.includes('group')) return 'Group Therapy Progress Note';
  return 'Progress Note';
}

/** Progress / intake / session documentation: status from signatures + supervised-provider rule (matches supervision queue semantics). */
function deriveSessionNoteStatus(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | undefined,
  author: Practitioner | undefined,
): ClinicalNote['noteStatus'] {
  const requiresCosign = practitionerRequiresCosignature(author);
  const hasProv = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
  const hasSup = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
  if (getRequestedEditsFromQR(qr)) return 'Draft';
  if (hasSup) return 'Signed';
  if (hasProv) {
    const cosignApplies = questionnaire
      ? isAppointmentTemplate(questionnaire)
      : isSessionDocumentationQr(qr);
    if (requiresCosign && cosignApplies) return 'Pending co-signature';
    return 'Signed';
  }
  return 'Draft';
}

function clinicalNoteUiStatus(noteStatus: ClinicalNote['noteStatus']): string {
  if (noteStatus === 'Signed') return 'Signed';
  if (noteStatus === 'Pending co-signature') return 'Pending co-signature';
  if (noteStatus === 'Complete') return 'Complete';
  return 'Draft';
}

function questionnaireTitle(qr: QuestionnaireResponse, questionnaires: Map<string, Questionnaire>): string {
  const ref = qr.questionnaire;
  if (typeof ref === 'string') {
    // Exact match covers full URLs and versioned canonical refs stored via the full qref key.
    if (questionnaires.has(ref)) {
      return questionnaires.get(ref)?.title ?? 'Questionnaire';
    }
    // Fall back to last path segment (e.g. "Questionnaire/{id}" or "…/phq-9|1.0.0").
    const seg = ref.includes('/') ? ref.split('/').pop() : ref;
    if (seg && questionnaires.has(seg)) {
      return questionnaires.get(seg)?.title ?? 'Questionnaire';
    }
  }
  return composedNoteTitle(qr) ?? 'Questionnaire';
}

/** Scored measures / PHQ / GAD — not session progress notes; no appointment linkage in seed. */
function measuresQrToClinicalNote(
  qr: QuestionnaireResponse,
  qTitle: string,
  episodeId: string | undefined,
  /** Care team / assigner shown in medical record (typically Task.requester, not panel default). */
  providerLabel: string,
  questionnaire: Questionnaire | undefined,
  observation: Observation | null | undefined,
  assessmentTaskId?: string,
): ClinicalNote {
  const day = qr.authored?.slice(0, 10) ?? '2026-01-01';
  // valueCoding answers don't sum via raw item values; resolve via Observation / QR total-score extension.
  const score = resolveAssessmentScore(qr, questionnaire, observation ?? undefined).score;
  const done = qr.status === 'completed';
  return {
    id: `note-qr-${qr.id}`,
    type: 'Assessments',
    noteType: 'Assessments',
    date: day,
    dateOfService: day,
    provider: providerLabel,
    providerType: 'Therapist',
    serviceType: qTitle,
    appointmentType: 'Assessment',
    serviceRendered: `${qTitle} — total score ${score}`,
    title: `Assessment: ${qTitle}`,
    status: done ? 'Complete' : 'Draft',
    noteStatus: done ? 'Complete' : 'Draft',
    billingStatus: 'N/A',
    episodeId,
    assessmentId: qr.id,
    assessmentTaskId,
    soapNote: {
      subjective: `Completed ${qTitle}.`,
      objective: `Total score: ${score}`,
      assessment: 'See scored measure interpretation guidelines.',
      plan: 'Monitor and reassess per protocol.',
    },
    signedDate: day,
    lastModified: qr.meta?.lastUpdated ?? `${day}T12:00:00`,
  };
}

function sessionQrToClinicalNote(
  qr: QuestionnaireResponse,
  questionnaire: Questionnaire | undefined,
  qTitle: string,
  episodeId: string | undefined,
  author: Practitioner | undefined,
  primaryProvider: string,
  appointment?: FhirAppointment,
): ClinicalNote {
  const isContactRoi = isContactRoiQuestionnaireResponse(qr);
  const resolvedEpisodeId = isContactRoi ? undefined : episodeId;
  const baseDay = qr.authored?.slice(0, 10) ?? qr.meta?.lastUpdated?.slice(0, 10) ?? '2026-01-01';
  const providerSig = getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
  const providerName = resolveClinicalNoteProviderDisplay(qr, author, primaryProvider);
  const apptId = getAppointmentIdFromQR(qr) ?? appointment?.id;
  const isGroupSession = appointment ? isCohortGroupAppointment(appointment) : false;
  const sharedNoteRef = appointment
    ? getAppointmentSharedNoteRef(appointment)?.reference
    : undefined;
  const groupTherapyFields =
    isGroupSession || sharedNoteRef
      ? {
          therapyServiceType: 'group' as const,
          ...(sharedNoteRef ? { groupSharedNoteReference: sharedNoteRef } : {}),
        }
      : {};

  if (isChartDocumentQuestionnaireResponse(qr)) {
    const noteStatus = deriveChartDocumentClinicalNoteStatus(qr, questionnaire, author);
    const clientSig = getSignatureFromQR(qr, CLIENT_SIGNATURE_EXT_URL);
    const displayDay =
      clientSig?.signedAt?.slice(0, 10)
      ?? providerSig?.signedAt?.slice(0, 10)
      ?? baseDay;
    const hasProv = !!providerSig;
    const hasClient = !!clientSig;
    const hasSup = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
    const isHistorical = qr.meta?.tag?.some(
      (t) =>
        t.system === HISTORICAL_MIGRATION_TAG_SYSTEM &&
        t.code === HISTORICAL_MIGRATION_TAG_CODES.historical,
    ) ?? false;

    return {
      id: `note-qr-${qr.id}`,
      type: qTitle,
      noteType: resolveChartDocumentRecordType(questionnaire, { isContactRoi }),
      date: displayDay,
      dateOfService: displayDay,
      provider: providerName,
      providerType: 'Therapist',
      serviceType: qTitle,
      appointmentType: 'Follow-up',
      serviceRendered: qTitle,
      title: qTitle,
      status: clinicalNoteUiStatus(noteStatus),
      noteStatus,
      billingStatus: 'Unbilled',
      episodeId: resolvedEpisodeId,
      ...(isContactRoi ? { patientLevel: true } : {}),
      // Intentionally omit appointmentId — chart documents must not be folded under a session row.
      ...groupTherapyFields,
      ...(isHistorical ? { isHistorical: true } : {}),
      soapNote: {
        subjective: 'Chart document stored in the linked questionnaire response.',
        objective: hasClient ? 'Client signature on file.' : '',
        assessment: isHistorical
          ? (hasProv || hasSup ? 'Imported historical signatures on file.' : 'Historical import (immutable).')
          : hasProv
            ? hasSup || noteStatus !== 'Pending co-signature'
              ? 'Provider signature on file.'
              : 'Provider signed; supervisor signature pending.'
            : 'Provider signature not yet recorded.',
        plan: '',
      },
      ...(hasProv ? { signedBy: providerName, signedDate: displayDay } : {}),
      lastModified: qr.meta?.lastUpdated ?? `${displayDay}T12:00:00`,
      ...(qr.id ? { chartDocumentResponseId: qr.id } : {}),
    };
  }

  const day = baseDay;
  const noteType = composedChartNoteType(qr) ?? sessionNoteTypeFromQuestionnaire(questionnaire);
  const noteStatus = deriveSessionNoteStatus(qr, questionnaire, author);
  const hasProv = !!providerSig;
  const hasSup = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
  // The note body lives in the response items; only fall back to the signature summary when the
  // response has no answers yet (e.g. a draft opened but never filled in).
  const { soapNote: answeredSoap, contentSections } = extractNoteContent(qr, questionnaire);
  const signatureSummary = hasProv
    ? hasSup || !practitionerRequiresCosignature(author)
      ? 'Provider signature on file.'
      : 'Provider signed; supervisor signature pending.'
    : 'Provider signature not yet recorded.';
  return {
    id: `note-qr-${qr.id}`,
    type: noteType,
    noteType,
    date: day,
    dateOfService: day,
    provider: providerName,
    providerType: 'Therapist',
    serviceType: qTitle,
    appointmentType: 'Follow-up',
    serviceRendered: qTitle,
    title: qTitle,
    status: clinicalNoteUiStatus(noteStatus),
    noteStatus,
    billingStatus: 'Unbilled',
    episodeId: resolvedEpisodeId,
    ...(isContactRoi ? { patientLevel: true } : {}),
    ...(apptId ? { appointmentId: apptId } : {}),
    ...groupTherapyFields,
    soapNote: answeredSoap ?? {
      subjective:
        'Structured documentation for this visit is stored in the linked questionnaire response. Open the appointment to view the full note.',
      objective: '',
      assessment: signatureSummary,
      plan: '',
    },
    ...(contentSections.length > 0 ? { contentSections } : {}),
    ...(hasProv ? { signedBy: providerName, signedDate: day } : {}),
    lastModified: qr.meta?.lastUpdated ?? `${day}T12:00:00`,
    ...(isChartDocumentQuestionnaireResponse(qr) && qr.id
      ? { chartDocumentResponseId: qr.id }
      : {}),
  };
}

function transferQrToClinicalNote(
  qr: QuestionnaireResponse,
  episodeId: string | undefined,
  author: Practitioner | undefined,
  primaryProvider: string,
  episode: EpisodeOfCare | undefined,
  serviceRequest: ServiceRequest | undefined,
  careManagerDisplay?: string,
): ClinicalNote {
  const day = qr.authored?.slice(0, 10) ?? '2026-01-01';
  const providerName = practitionerDisplayName(author, primaryProvider);
  const episodeDisplay = episode?.type?.[0]
    ? displayLabelForEpisodeType(episode.type[0])
    : undefined;
  const episodeForContext =
    episode && careManagerDisplay && !episode.careManager?.display
      ? {
          ...episode,
          careManager: episode.careManager
            ? { ...episode.careManager, display: careManagerDisplay }
            : episode.careManager,
        }
      : episode;
  const context = { qr, serviceRequest, episode: episodeForContext, episodeDisplay };
  const { status, noteStatus } = transferChartNoteStatus(context);
  const serviceRendered = transferServiceRenderedLabel(context);

  return {
    id: `note-qr-${qr.id}`,
    type: 'Transfer Request',
    noteType: 'Transfer Request',
    date: day,
    dateOfService: day,
    provider: providerName,
    providerType: 'Therapist',
    serviceType: 'Care transfer',
    appointmentType: 'Transfer',
    serviceRendered,
    title: 'Transfer Request',
    status,
    noteStatus,
    billingStatus: 'N/A',
    episodeId,
    soapNote: buildTransferClinicalNoteSoap(context),
    lastModified: qr.meta?.lastUpdated ?? `${day}T12:00:00`,
  };
}

function resolveSourceTaskIdForQr(
  qr: QuestionnaireResponse,
  qrIdToTaskId: Map<string, string>,
): string | undefined {
  if (qr.id) {
    const mapped = qrIdToTaskId.get(qr.id);
    if (mapped) return mapped;
  }
  const basedOnRef = getLinkedTaskId(qr);
  return basedOnRef;
}

function qrToCompletedForm(
  qr: QuestionnaireResponse,
  qTitle: string,
  episodeId?: string,
  sourceTaskId?: string,
): CompletedForm {
  const rows: { question: string; answer: string }[] = [];
  const walk = (items: typeof qr.item): void => {
    for (const it of items ?? []) {
      const q = it.text ?? it.linkId ?? 'Question';
      for (const ans of it.answer ?? []) {
        const v =
          ans.valueString ??
          (ans.valueInteger != null ? String(ans.valueInteger) : undefined) ??
          (ans.valueDecimal != null ? String(ans.valueDecimal) : undefined) ??
          (ans.valueCoding?.display ?? ans.valueCoding?.code);
        if (v != null) rows.push({ question: q, answer: v });
      }
      if (it.item) walk(it.item);
    }
  };
  walk(qr.item);
  return {
    id: `form-${qr.id}`,
    name: qTitle,
    completedDate: qr.authored?.slice(0, 10) ?? '2026-01-01',
    questions: rows.length > 0 ? rows : [{ question: 'Response', answer: 'Completed' }],
    ...(episodeId ? { episodeId } : {}),
    ...(qr.id ? { questionnaireResponseId: qr.id } : {}),
    ...(sourceTaskId ? { sourceTaskId } : {}),
  };
}

function fhirTaskToUi(task: FhirTask, episodeId?: string): Task | null {
  if (
    !task.id ||
    !isFormRequestTask(task) ||
    task.status === 'cancelled' ||
    task.status === 'completed'
  ) {
    return null;
  }
  return {
    id: task.id,
    type: 'form',
    name: getFormTaskDisplayName(task),
    status: resolveChartFormTaskStatus(task),
    requestedDate: task.authoredOn?.slice(0, 10),
    dueDate: task.restriction?.period?.end?.slice(0, 10),
    ...(episodeId ? { episodeId } : {}),
  };
}

function sortChartOverviewTasks(a: Task, b: Task): number {
  const rank = (status: Task['status']) => {
    if (status === 'completed') return 1;
    return 0;
  };
  const rankDiff = rank(a.status) - rank(b.status);
  if (rankDiff !== 0) return rankDiff;
  return (b.requestedDate ?? '').localeCompare(a.requestedDate ?? '');
}

function docRefToAdminNote(doc: DocumentReference): AdminNote {
  const typeCode = doc.type?.coding?.[0]?.code ?? 'admin-note';
  const noteType = typeCode === 'admin-note' ? 'Admin' : 'Internal';
  const episodeId = relatedEpisodeIdFromDocument(doc);
  return {
    id: `admin-${doc.id}`,
    content: doc.description ?? 'Administrative note',
    createdBy: doc.author?.[0]?.display?.trim() || 'Provider',
    createdAt: doc.date ?? '2026-01-01T00:00:00',
    type: noteType as AdminNote['type'],
    recordType: 'Admin Notes',
    ...(episodeId ? { episodeId } : {}),
  };
}

function mapDocumentCategory(doc: DocumentReference): ClientDocument['category'] | undefined {
  const catCode = doc.category?.[0]?.coding?.[0]?.code?.toLowerCase();
  if (catCode === 'insurance') return 'Insurance';
  if (catCode === 'id' || catCode === 'identification') return 'Identification';
  if (catCode === 'consent') return 'Consent';
  if (catCode === 'clinical' || catCode === 'admin') return 'Clinical';
  if (catCode === 'other') return 'Other';
  const title = (doc.content?.[0]?.attachment?.title ?? doc.description ?? '').toLowerCase();
  if (title.includes('insurance')) return 'Insurance';
  if (title.includes('consent')) return 'Consent';
  if (title.includes('identification') || title.includes(' id ') || title.includes('license')) return 'Identification';
  return undefined;
}

function attachmentSizeLabel(attachment: NonNullable<DocumentReference['content']>[number]['attachment']): string {
  if (attachment?.size != null) {
    return `${Math.round(attachment.size / 1024)} KB`;
  }
  if (attachment?.data) {
    const bytes = Math.floor((attachment.data.length * 3) / 4);
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
  return '—';
}

function docRefToHistoricalChartRecord(doc: DocumentReference): HistoricalChartRecord | null {
  if (!doc.id) return null;
  const recordType = getHistoricalMedicalRecordType(doc) ?? 'Intake Forms';
  const attachment = doc.content?.[0]?.attachment;
  const episodeId = relatedEpisodeIdFromDocument(doc);
  const dateOfService =
    doc.date?.slice(0, 10) ??
    doc.context?.period?.start?.slice(0, 10) ??
    doc.meta?.lastUpdated?.slice(0, 10) ??
    '';
  return {
    id: doc.id,
    documentReferenceId: doc.id,
    title: attachment?.title ?? doc.description ?? 'Historical record',
    recordType,
    dateOfService,
    provider: doc.author?.[0]?.display?.trim() || 'Provider',
    status: 'Historical',
    ...(episodeId ? { episodeId } : {}),
    ...(attachment
      ? {
        attachment: {
          contentType: attachment.contentType,
          url: attachment.url,
          title: attachment.title,
        },
      }
      : {}),
  };
}

function docRefToClientDocument(doc: DocumentReference): ClientDocument {
  const attachment = doc.content?.[0]?.attachment;
  const contentType = attachment?.contentType ?? 'application/pdf';
  const docType = contentType.startsWith('image/') ? 'Image' : contentType === 'application/pdf' ? 'PDF' : 'Document';
  const episodeId = relatedEpisodeIdFromDocument(doc);
  const sharedWithClient = isClientVisibleDocumentReference(doc);
  const category = mapDocumentCategory(doc);
  return {
    id: doc.id ?? `doc-${Date.now()}`,
    name: attachment?.title ?? doc.description ?? 'Document',
    uploadedDate: attachment?.creation ?? doc.date?.slice(0, 10) ?? '2026-01-01',
    uploadedBy: doc.author?.[0]?.display?.trim() || 'Provider',
    size: attachmentSizeLabel(attachment),
    type: docType,
    ...(category ? { category } : {}),
    status: sharedWithClient ? 'Shared' : (doc.status === 'current' ? 'Stored' : 'Pending'),
    ...(episodeId ? { episodeId } : {}),
    ...(doc.id ? { documentReferenceId: doc.id } : {}),
    sharedWithClient,
    ...(attachment ? {
      attachment: {
        contentType: attachment.contentType,
        url: attachment.url,
        data: attachment.data,
        title: attachment.title,
        size: attachment.size,
      },
    } : {}),
  };
}

function docRefToDischargeSummary(doc: DocumentReference): DischargeSummary | null {
  const eocRef = doc.context?.related?.[0]?.reference?.replace('EpisodeOfCare/', '');
  if (!eocRef) return null;
  const day = doc.date?.slice(0, 10) ?? '2026-01-01';
  let parsed: { reasonForDischarge?: string; summaryOfCare?: string; recommendations?: string } = {};
  const b64 = doc.content?.[0]?.attachment?.data;
  if (b64) {
    try {
      const json = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('utf8');
      parsed = JSON.parse(json);
    } catch { /* use defaults */ }
  }
  return {
    id: `ds-${doc.id}`,
    episodeId: eocRef,
    dischargeDate: day,
    signedBy: doc.author?.[0]?.display,
    signedAt: doc.date,
    reasonForDischarge: parsed.reasonForDischarge ?? doc.description ?? 'Treatment completed',
    summaryOfCare: parsed.summaryOfCare ?? 'See clinical documentation.',
    recommendations: parsed.recommendations ?? 'Follow-up as needed.',
  };
}

/**
 * Load chart-shaped data for a patient from Medplum (pilot / local seed friendly).
 */
export async function fetchMedplumClientChartBundle(
  medplum: MedplumClient,
  patientId: string,
): Promise<ClientChartBundle> {
  const bundle = emptyBundle();
  const patientRef = `Patient/${patientId}`;
  const compartment = patientCompartmentSearch(patientId);

  const taskForPatient = (t: FhirTask) => t.for?.reference === patientRef;

  const [eocs, fhirAppts, encounters, qrs, observations, openTasksRaw, completedTasksRaw, documentRefs, serviceRequests] =
    await Promise.all([
      medplum.searchResources('EpisodeOfCare', { ...compartment, _count: '50' }),
      medplum.searchResources('Appointment', { ...compartment, _count: '200' }),
      medplum.searchResources('Encounter', { ...compartment, _count: '200' }),
      medplum.searchResources('QuestionnaireResponse', { ...compartment, _count: '200' }),
      medplum.searchResources('Observation', { ...compartment, _count: '200' }),
      medplum
        .searchResources('Task', { ...compartment, status: 'requested,in-progress,ready', _count: '200' })
        .catch(() => [] as FhirTask[]),
      medplum
        .searchResources('Task', { ...compartment, status: 'completed', _count: '200' })
        .catch(() => [] as FhirTask[]),
      medplum.searchResources('DocumentReference', { ...compartment, _count: '200' }).catch(() => [] as DocumentReference[]),
      medplum
        .searchResources('ServiceRequest', { ...compartment, _count: '100' })
        .catch(() => [] as ServiceRequest[]),
    ]);

  const fhirTasks = openTasksRaw.filter(taskForPatient);
  const completedTasks = completedTasksRaw.filter(taskForPatient);

  // EoC panel: resolve `EpisodeOfCare.patient` and `Account.guarantor` to real names
  // (and keep Primary / Supporting from `primaryClinicalPatient` vs current chart id).
  bundle.episodes = await buildEpisodesWithResolvedParties(medplum, eocs, patientId);

  const eocById = new Map(eocs.filter((e) => e.id).map((e) => [e.id!, e]));
  const serviceRequestByEpisodeId = indexServiceRequestsByEpisodeId(serviceRequests);

  const apptToEpisode = new Map<string, string>();
  const encounterIdToEoc = new Map<string, string>();
  for (const enc of encounters) {
    const eocRef = enc.episodeOfCare?.[0]?.reference?.replace('EpisodeOfCare/', '');
    if (enc.id && eocRef) {
      encounterIdToEoc.set(enc.id, eocRef);
    }
    if (!eocRef) {
      continue;
    }
    for (const ar of enc.appointment ?? []) {
      const aid = ar.reference?.replace('Appointment/', '');
      if (aid) {
        apptToEpisode.set(aid, eocRef);
      }
    }
  }
  for (const a of fhirAppts) {
    if (!a.id || apptToEpisode.has(a.id)) continue;
    const fromExt = episodeIdFromFhirExtension(a);
    if (fromExt) apptToEpisode.set(a.id, fromExt);
  }

  bundle.tasks = fhirTasks
    .map((t) => fhirTaskToUi(t, episodeIdFromFhirExtension(t)))
    .filter((t): t is Task => t != null)
    .sort(sortChartOverviewTasks);

  // Map QR id → Task id from completed tasks (for assessment routing in Medical Record & Measures log)
  const qrIdToTaskId = new Map<string, string>();
  for (const ct of completedTasks) {
    if (!ct.id) continue;
    for (const out of ct.output ?? []) {
      const ref = out.valueReference?.reference;
      if (ref?.startsWith('QuestionnaireResponse/')) {
        qrIdToTaskId.set(ref.replace('QuestionnaireResponse/', ''), ct.id);
      }
    }
  }
  // Fallback: portal/bot submissions stamp PORTAL_TASK_REFERENCE_EXT (legacy QR.basedOn → Task)
  for (const qr of qrs) {
    if (!qr.id || qrIdToTaskId.has(qr.id)) continue;
    const tid = getLinkedTaskId(qr);
    if (tid) {
      qrIdToTaskId.set(qr.id, tid);
    }
  }

  const completedTaskById = new Map<string, FhirTask>();
  for (const ct of completedTasks) {
    if (ct.id) completedTaskById.set(ct.id, ct);
  }

  // Build pending form tasks: only Tasks classified as form requests (not assessments).
  for (const t of fhirTasks) {
    if (!t.id || !isFormRequestTask(t)) continue;

    const dueStr = t.restriction?.period?.end?.slice(0, 10);
    const now = new Date();
    const isOverdue = dueStr ? new Date(dueStr + 'T23:59:59').getTime() < now.getTime() : false;
    const taskStatus = isOverdue ? 'overdue' : (t.status === 'in-progress' ? 'in-progress' : 'requested');

    const epId = episodeIdFromFhirExtension(t);
    const epTypeCc = epId ? eocs.find((e) => e.id === epId)?.type?.[0] : undefined;
    const epLabel = epTypeCc ? displayLabelForEpisodeType(epTypeCc) : undefined;

    bundle.pendingFormTasks.push({
      id: `pending-${t.id}`,
      taskId: t.id,
      name: getFormTaskDisplayName(t),
      status: taskStatus as PendingFormTask['status'],
      requestedDate: t.authoredOn?.slice(0, 10),
      dueDate: dueStr,
      requester: t.requester?.display,
      episodeLabel: epLabel,
    });
  }

  const primaryProvider =
    eocs[0]?.careManager?.display ??
    eocs[0]?.careManager?.reference?.replace('Practitioner/', '') ??
    'Provider';

  /** Session/progress-note QRs and the appointment they document (tag or EoC + date fallback). */
  const chartNoteAppointmentByQrId = collectChartNoteAppointmentLinks(qrs, fhirAppts, apptToEpisode);
  const appointmentIdsWithQr = new Set(chartNoteAppointmentByQrId.values());

  const authorPractitionerIds = new Set<string>();
  for (const qr of qrs) {
    const ref = qr.author?.reference;
    if (ref?.startsWith('Practitioner/')) {
      authorPractitionerIds.add(ref.replace('Practitioner/', ''));
    }
  }
  for (const a of fhirAppts) {
    for (const p of a.participant ?? []) {
      const ref = p.actor?.reference;
      if (ref?.startsWith('Practitioner/')) {
        authorPractitionerIds.add(ref.replace('Practitioner/', ''));
      }
    }
  }
  for (const ct of completedTasks) {
    const pref = ct.requester?.reference;
    if (pref?.startsWith('Practitioner/')) {
      authorPractitionerIds.add(pref.replace('Practitioner/', ''));
    }
  }
  for (const eoc of eocs) {
    const cmRef = eoc.careManager?.reference;
    if (cmRef?.startsWith('Practitioner/')) {
      authorPractitionerIds.add(cmRef.replace('Practitioner/', ''));
    }
  }

  const qRefs = new Set<string>();
  for (const qr of qrs) {
    const qref = qr.questionnaire;
    if (typeof qref === 'string') {
      qRefs.add(qref);
    }
  }
  const questionnaires = new Map<string, Questionnaire>();
  for (const qref of qRefs) {
    const q = await resolveQuestionnaireForResponseRef(medplum, qref);
    if (q?.id) {
      questionnaires.set(q.id, q);
      // Store by last path segment (handles "Questionnaire/{id}" references)
      const seg = qref.includes('/') ? qref.split('/').pop() : qref;
      if (seg) questionnaires.set(seg, q);
      // Store by full original ref (handles versioned canonical URLs like "https://…|1.0.0")
      questionnaires.set(qref, q);
    }
  }

  const practitionerById = new Map<string, Practitioner>();
  for (const pid of authorPractitionerIds) {
    try {
      const p = await medplum.readResource('Practitioner', pid);
      practitionerById.set(pid, p);
    } catch {
      /* ignore missing practitioner */
    }
  }

  for (const a of fhirAppts) {
    if (!a.id) continue;
    const eid = resolveChartAppointmentEpisodeId(a, apptToEpisode, eocs);
    const eoc = eid ? eocById.get(eid) : undefined;
    const episodeTypeLabel = chartAppointmentServiceTypeLabel(a, eoc);
    bundle.appointments.push(
      fhirAppointmentToUi(
        a,
        eid,
        primaryProvider,
        appointmentIdsWithQr.has(a.id),
        episodeTypeLabel,
        practitionerById,
      ),
    );
  }

  const qrCoverageKeys = new Set<string>();

  for (const qr of qrs) {
    if (!qr.id) continue;
    const title = questionnaireTitle(qr, questionnaires);
    const isContactRoi = isContactRoiQuestionnaireResponse(qr);
    const eocId = resolveQuestionnaireResponseEpisodeId(
      qr,
      encounterIdToEoc,
      apptToEpisode,
      isContactRoi ? undefined : eocs[0]?.id,
    );

    const qref = qr.questionnaire;
    // Resolve the Questionnaire object: try exact ref first (handles versioned canonical URLs),
    // then fall back to last path segment (handles "Questionnaire/{id}" resource refs).
    let questionnaire: Questionnaire | undefined;
    if (typeof qref === 'string') {
      questionnaire = questionnaires.get(qref);
      if (!questionnaire) {
        const seg = qref.includes('/') ? qref.split('/').pop() : qref;
        if (seg) questionnaire = questionnaires.get(seg);
      }
    }
    const authorRef = qr.author?.reference?.replace('Practitioner/', '');
    const authorPractitioner = authorRef ? practitionerById.get(authorRef) : undefined;

    const taskIdForQrInner = qr.id ? qrIdToTaskId.get(qr.id) : undefined;
    const linkedTaskForQr = taskIdForQrInner ? completedTaskById.get(taskIdForQrInner) : undefined;

    const isChartDocQr = isChartDocumentQuestionnaireResponse(qr);
    const isFormQr =
      !isChartDocQr &&
      ((linkedTaskForQr != null && isFormRequestTask(linkedTaskForQr)) ||
        isClientFacingDocumentQuestionnaire(questionnaire, qr));
    const isClientFormQr = isFormQr && !isClinicalQuestionnaire(questionnaire, qr);
    const isTransferQr = isTransferQuestionnaireResponse(qr, questionnaire);

    const matchedObs =
      questionnaire && isMeasuresQuestionnaire(questionnaire)
        ? matchObservationForQuestionnaireResponse(observations, qr, questionnaire)
        : undefined;

    if (isTransferQr) {
      const episode = eocId ? eocById.get(eocId) : undefined;
      const serviceRequest = eocId ? serviceRequestByEpisodeId.get(eocId) : undefined;
      const careManagerId = episode?.careManager?.reference?.replace('Practitioner/', '');
      const careManagerDisplay = careManagerId
        ? practitionerDisplayName(
            careManagerId ? practitionerById.get(careManagerId) : undefined,
            episode?.careManager?.display ?? careManagerId,
          )
        : undefined;
      bundle.clinicalNotes.push(
        transferQrToClinicalNote(
          qr,
          eocId,
          authorPractitioner,
          primaryProvider,
          episode,
          serviceRequest,
          careManagerDisplay,
        ),
      );
    } else if (questionnaire && isMeasuresQuestionnaire(questionnaire)) {
      let providerLabel = taskRequesterLabel(linkedTaskForQr?.requester, practitionerById);
      if (providerLabel === '—') {
        providerLabel = practitionerDisplayName(authorPractitioner, primaryProvider);
      }
      bundle.clinicalNotes.push(
        measuresQrToClinicalNote(qr, title, eocId, providerLabel, questionnaire, matchedObs, taskIdForQrInner),
      );
    } else if (!isClientFormQr) {
      const linkedApptId = qr.id ? chartNoteAppointmentByQrId.get(qr.id) : undefined;
      const linkedAppt = linkedApptId
        ? fhirAppts.find((a) => a.id === linkedApptId)
        : undefined;
      bundle.clinicalNotes.push(
        sessionQrToClinicalNote(
          qr,
          questionnaire,
          title,
          eocId,
          authorPractitioner,
          primaryProvider,
          linkedAppt,
        ),
      );
    }

    // Only push to completedForms when the QR represents a client-facing form:
    // - QR is basedOn a form request Task, OR
    // - Questionnaire is explicitly client-facing (forms/consent/intake categories)
    // Exclude: clinical notes, session progress notes, measures/assessments, transfer context
    if (isFormQr && !isClinicalQuestionnaire(questionnaire, qr) && !isTransferQr) {
      const sourceTaskId = resolveSourceTaskIdForQr(qr, qrIdToTaskId);
      bundle.completedForms.push(qrToCompletedForm(qr, title, eocId, sourceTaskId));
    }

    if (!questionnaire || !isMeasuresQuestionnaire(questionnaire)) {
      continue;
    }

    const typeLabel = assessmentTypeLabelFromQuestionnaire(questionnaire);
    const day = qr.authored?.slice(0, 10) ?? '2026-01-01';
    const resolved = resolveAssessmentScore(qr, questionnaire, matchedObs ?? undefined);
    const maxScore = resolveMaxScoreForQuestionnaire(questionnaire);
    const severity = resolveInterpretationLabel({
      qr,
      questionnaire,
      score: resolved.score,
      observation: matchedObs ?? undefined,
    });

    const linkedTaskId = taskIdForQrInner;
    const linkedTask = linkedTaskForQr;
    const assignedByLabel = taskRequesterLabel(linkedTask?.requester, practitionerById);
    let measuresProviderLabel = assignedByLabel;
    if (measuresProviderLabel === '—') {
      measuresProviderLabel = practitionerDisplayName(authorPractitioner, primaryProvider);
    }
    bundle.assessments.push({
      id: `qr-${qr.id}`,
      ...(linkedTaskId ? { assessmentTaskId: linkedTaskId } : {}),
      assessmentType: typeLabel,
      date: day,
      time: '',
      provider: measuresProviderLabel,
      score: resolved.score,
      maxScore,
      ...(severity ? { severity } : {}),
      administeredBy: linkedTaskId ? assignedByLabel : '—',
      status: qr.status === 'completed' ? 'Completed' : 'In Progress',
      ...(eocId ? { episodeId: eocId } : {}),
    });

    qrCoverageKeys.add(assessmentCoverageKey(typeLabel, day));
    const loinc = loincCodeForQuestionnaire(questionnaire);
    if (loinc && LOINC_ASSESSMENT_SCORE[loinc]) {
      qrCoverageKeys.add(assessmentCoverageKey(LOINC_ASSESSMENT_SCORE[loinc].assessmentType, day));
    }
  }

  for (const obs of observations) {
    const eocId = episodeIdFromFhirExtension(obs) ?? eocs[0]?.id;
    const a = observationToAssessmentView(obs, primaryProvider, eocId);
    if (!a) continue;
    const key = assessmentCoverageKey(a.assessmentType, a.date.slice(0, 10));
    if (qrCoverageKeys.has(key)) continue;
    bundle.assessments.push(a);
  }

  const DOC_TYPE_ADMIN = 'admin-note';
  const DOC_TYPE_DISCHARGE = 'discharge-summary';
  const DOC_TYPE_CLINICAL = 'clinical-document';

  for (const doc of documentRefs) {
    // Private psychotherapy notes (including historical PDF bundles) stay off Medical Record.
    if (isPsychotherapyNote(doc)) {
      continue;
    }
    if (isHistoricalChartMigrationDocumentReference(doc)) {
      const historical = docRefToHistoricalChartRecord(doc);
      if (historical) {
        bundle.historicalRecords.push(historical);
      }
      continue;
    }
    const typeCode = doc.type?.coding?.[0]?.code;
    if (typeCode === DOC_TYPE_ADMIN) {
      bundle.adminNotes.push(docRefToAdminNote(doc));
    } else if (typeCode === DOC_TYPE_DISCHARGE) {
      const ds = docRefToDischargeSummary(doc);
      if (ds) bundle.dischargeSummaries.push(ds);
    } else if (typeCode === DOC_TYPE_CLINICAL) {
      bundle.documents.push(docRefToClientDocument(doc));
    } else {
      bundle.documents.push(docRefToClientDocument(doc));
    }
  }

  const trendRows = buildAssessmentTrendDataPoints(bundle.assessments);
  bundle.assessmentTrends = trendRows.length > 0 ? trendRows : bundle.assessmentTrends;

  const notesByApptId = new Map<string, ClinicalNote>();
  for (const note of bundle.clinicalNotes) {
    if (note.appointmentId) notesByApptId.set(note.appointmentId, note);
  }
  bundle.appointments = bundle.appointments.map((apt) => ({
    ...apt,
    hasNote: apt.hasNote || (apt.id ? notesByApptId.has(apt.id) : false),
  }));

  return bundle;
}
